import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import MainLayout from '../../layouts/MainLayout.tsx';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { IncidentResponse } from '../../Interfaces/types.ts';

import kmc from '../../assets/kmc.png';

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-blue-900 text-white text-xs font-bold px-2 py-1">
    {children}
  </div>
);

const TableRow = ({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) => (
  <div className="flex text-xs border-b border-gray-300 w-full">
    <div className="w-1/3 font-semibold px-2 py-1 bg-gray-100">{label}</div>
    <div className="w-2/3 px-2 py-1">{value || 'N/A'}</div>
  </div>
);

const IncidentDetail: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const incident = (state && state.incident) as IncidentResponse | undefined;

  if (!incident) {
    return (
      <MainLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-blue-900 text-white">
          <div className="text-lg font-bold mb-4">No incident data found.</div>
          <button
            onClick={() => navigate(-1)}
            className="bg-white text-blue-900 px-4 py-2 rounded font-semibold"
          >
            Back
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900 pb-32 px-4 md:px-8">
        {/* Header */}
        <div className="pt-6">
          <h1 className="text-white text-lg font-medium">Detailed Report</h1>
        </div>

        {/* Card */}
        <div className="mt-4 bg-white rounded-t-3xl pb-10 w-full">
          {/* Logo */}
          <div className="flex justify-center pt-4">
            <img src={kmc} alt="KMC Logo" className="h-10" />
          </div>

          {/* Title */}
          <div className="text-center font-bold text-blue-900 text-base mt-2 mb-4">
            Equipment Incident Report Form
          </div>

          {/* Table wrapper: full width on all screens, horizontal scroll on small */}
          <div className="border border-black rounded overflow-x-auto bg-white">
            <div className="min-w-full">
              {/* Basic Equipment Information */}
              <SectionHeader>Basic Equipment Information</SectionHeader>
              <TableRow label="Machine ID" value={incident.equipment} />
              <TableRow label="Machine Name" value={incident.equipment} />
              <TableRow
                label="Device Model/Specification"
                value={incident.model}
              />
              <TableRow label="Workshop" value={incident.work_shop} />
              <TableRow
                label="Machine Function"
                value={incident.machine_function}
              />

              {/* Basic Accident/Incident Information */}
              <SectionHeader>Basic Accident/Incident Information</SectionHeader>
              <TableRow
                label="Accident/Incident type"
                value={incident.incident_type}
              />
              <TableRow label="Cause of the incident" value={incident.cause} />
              <TableRow
                label="Equipment Operator"
                value={incident.operator}
              />
              <TableRow
                label="Time of Accident/Incident"
                value={incident.date_and_time}
              />
              <TableRow
                label="Description of damage"
                value={incident.description}
              />

              {/* Cause of Loss Statement */}
              <SectionHeader>Cause of Loss Statement</SectionHeader>
              <div className="flex text-xs border-b border-gray-300 w-full min-w-full">
                <div className="w-1/4 font-semibold px-2 py-1 bg-gray-100">
                  Downtime
                </div>
                <div className="w-1/4 font-semibold px-2 py-1 bg-gray-100">
                  Repair Fee
                </div>
                <div className="w-1/4 font-semibold px-2 py-1 bg-gray-100">
                  Production Loss
                </div>
                <div className="w-1/4 font-semibold px-2 py-1 bg-gray-100">
                  Remark
                </div>
              </div>
              <div className="flex text-xs border-b border-gray-300 w-full min-w-full">
                <div className="w-1/4 px-2 py-1">{incident.down_time}</div>
                <div className="w-1/4 px-2 py-1">{incident.repair_fee}</div>
                <div className="w-1/4 px-2 py-1">{incident.production_loss}</div>
                <div className="w-1/4 px-2 py-1">{incident.remarks}</div>
              </div>

              {/* Problem Definition */}
              <SectionHeader>Problem Definition</SectionHeader>
              <TableRow
                label="Primary Cause"
                value={incident.problem}
              />
              <TableRow label="Why 1" value={incident.why1} />
              <TableRow label="Why 2" value={incident.why2} />
              <TableRow label="Why 3" value={incident.why3} />
              <TableRow label="Root Cause" value={incident.why4} />

              {/* Corrective Action */}
              <SectionHeader>Corrective Action</SectionHeader>
              <TableRow label="Action" value={incident.action} />
              <TableRow
                label="Responsible Party"
                value={incident.party_responsible}
              />
              <div className="flex text-xs border-b border-gray-300 w-full min-w-full">
                <div className="w-1/2 font-semibold px-2 py-1 bg-gray-100">
                  Date to Begin
                </div>
                <div className="w-1/2 font-semibold px-2 py-1 bg-gray-100">
                  Date to Complete
                </div>
              </div>
              <div className="flex text-xs border-b border-gray-300 w-full min-w-full">
                <div className="w-1/2 px-2 py-1">{incident.start_date}</div>
                <div className="w-1/2 px-2 py-1">{incident.end_date}</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-blue-900 font-semibold text-sm mt-4">
            Page 1 of 1
          </div>
        </div>
      </div>

      <ClientBottomNav showBack={true} />
    </MainLayout>
  );
};

export default IncidentDetail;
